import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DisplayApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/applications`
      );

      if (response.data.success) {
        setApplications(response.data.data);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to load applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}`);
        setApplications(applications.filter(app => app.id !== id));
        alert('Application deleted successfully');
      } catch (error) {
        console.error('Error deleting application:', error);
        alert('Failed to delete application');
      }
    }
  };

  // Filter applications based on search term
  const filteredApplications = applications.filter(application => {
    const matchesSearch = 
      application.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.mobile.includes(searchTerm) ||
      application.qualification.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.hospital.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="mt-4 text-lg">Loading applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="alert alert-error max-w-md">
          <div>
            <h3 className="font-bold">Error</h3>
            <p>{error}</p>
            <button 
              onClick={fetchApplications} 
              className="btn btn-outline btn-sm mt-2"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="card bg-base-100 shadow-xl mb-6">
          <div className="card-body text-center">
            <h1 className="card-title justify-center text-3xl font-bold text-primary">
              Palliative Care Course Applications
            </h1>
            <p className="text-lg text-base-content/70">
              Total Applications: <span className="font-semibold text-primary">{applications.length}</span>
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="card bg-base-100 shadow-xl mb-6">
          <div className="card-body">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              <div className="form-control flex-1 w-full">
                <div className="join w-full">
                  <input
                    type="text"
                    placeholder="Search by name, email, mobile, qualification, or hospital..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input input-bordered join-item flex-1 w-full"
                  />
                  <button className="btn btn-primary join-item">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search
                  </button>
                </div>
              </div>
              <button 
                onClick={fetchApplications} 
                className="btn btn-outline btn-success"
                title="Refresh applications"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Applications Table */}
        {filteredApplications.length === 0 ? (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body text-center py-12">
              {applications.length === 0 ? (
                <div>
                  <h3 className="text-2xl font-semibold text-base-content/70 mb-4">
                    No Applications Found
                  </h3>
                  <p className="text-base-content/60">
                    There are no course applications submitted yet.
                  </p>
                </div>
              ) : (
                <div>
                  <h3 className="text-2xl font-semibold text-base-content/70 mb-4">
                    No Matching Applications
                  </h3>
                  <p className="text-base-content/60">
                    No applications match your search criteria.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body p-0">
              <div className="overflow-x-auto">
                <table className="table table-zebra">
                  <thead>
                    <tr className="bg-base-200">
                      <th className="text-base font-semibold">ID</th>
                      <th className="text-base font-semibold">Name</th>
                      <th className="text-base font-semibold">Email</th>
                      <th className="text-base font-semibold">Mobile</th>
                      <th className="text-base font-semibold">Qualification</th>
                      <th className="text-base font-semibold">Designation</th>
                      <th className="text-base font-semibold">Hospital</th>
                      <th className="text-base font-semibold">Applied On</th>
                      <th className="text-base font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((application) => (
                      <tr key={application.id} className="hover:bg-base-300/50">
                        <td className="font-mono text-sm">#{application.id}</td>
                        <td>
                          <div>
                            <div className="font-semibold">{application.name}</div>
                            <div className="text-xs text-base-content/60 flex items-center gap-1 mt-1">
                              {application.gender === 'male' ? '♂' : '♀'}
                              {application.dob && (
                                <>
                                  <span>•</span>
                                  <span>{calculateAge(application.dob)} yrs</span>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <a 
                            href={`mailto:${application.email}`}
                            className="link link-primary"
                          >
                            {application.email}
                          </a>
                        </td>
                        <td>
                          <a 
                            href={`tel:${application.mobile}`}
                            className="link link-primary"
                          >
                            {application.mobile}
                          </a>
                        </td>
                        <td>{application.qualification}</td>
                        <td>
                          <span className="badge badge-outline badge-primary">
                            {application.designation}
                          </span>
                        </td>
                        <td>{application.hospital}</td>
                        <td>{formatDate(application.created_at)}</td>
                        <td>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewDetails(application)}
                              className="btn btn-sm btn-info"
                              title="View Details"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </button>
                            <button
                              onClick={() => handleDelete(application.id)}
                              className="btn btn-sm btn-error"
                              title="Delete Application"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Application Details Modal */}
        <dialog className={`modal ${showModal ? 'modal-open' : ''}`}>
          <div className="modal-box max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="modal-header flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-primary">Application Details</h2>
              <button 
                onClick={handleCloseModal}
                className="btn btn-sm btn-circle btn-ghost"
              >
                ✕
              </button>
            </div>
            
            {selectedApplication && (
              <div className="modal-body space-y-6">
                {/* Personal Information */}
                <div className="card bg-base-200">
                  <div className="card-body">
                    <h3 className="card-title text-lg font-semibold mb-4">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Name</span>
                        </label>
                        <p className="text-base-content">{selectedApplication.name}</p>
                      </div>
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Email</span>
                        </label>
                        <a 
                          href={`mailto:${selectedApplication.email}`}
                          className="link link-primary"
                        >
                          {selectedApplication.email}
                        </a>
                      </div>
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Mobile</span>
                        </label>
                        <a 
                          href={`tel:${selectedApplication.mobile}`}
                          className="link link-primary"
                        >
                          {selectedApplication.mobile}
                        </a>
                      </div>
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Gender</span>
                        </label>
                        <p className="text-base-content">
                          {selectedApplication.gender === 'male' ? 'Male ♂' : 'Female ♀'}
                        </p>
                      </div>
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Date of Birth</span>
                        </label>
                        <p className="text-base-content">
                          {formatDate(selectedApplication.dob)}
                          {selectedApplication.dob && (
                            <span className="text-base-content/60 ml-2">
                              ({calculateAge(selectedApplication.dob)} years)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div className="card bg-base-200">
                  <div className="card-body">
                    <h3 className="card-title text-lg font-semibold mb-4">Professional Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Qualification</span>
                        </label>
                        <p className="text-base-content">{selectedApplication.qualification}</p>
                      </div>
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Registration Number</span>
                        </label>
                        <p className="text-base-content">
                          {selectedApplication.registration_number || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Designation</span>
                        </label>
                        <p className="text-base-content">{selectedApplication.designation}</p>
                      </div>
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Hospital/Clinic</span>
                        </label>
                        <p className="text-base-content">{selectedApplication.hospital}</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="label">
                          <span className="label-text font-semibold">Previous Training</span>
                        </label>
                        <p className="text-base-content">
                          {selectedApplication.experience || 'No previous training'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address & Course Details */}
                <div className="card bg-base-200">
                  <div className="card-body">
                    <h3 className="card-title text-lg font-semibold mb-4">Address & Course Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="label">
                          <span className="label-text font-semibold">Address</span>
                        </label>
                        <p className="text-base-content whitespace-pre-line">
                          {selectedApplication.address}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="label">
                            <span className="label-text font-semibold">Course</span>
                          </label>
                          <p className="text-base-content">{selectedApplication.course}</p>
                        </div>
                        <div>
                          <label className="label">
                            <span className="label-text font-semibold">Course Fee</span>
                          </label>
                          <p className="text-base-content">₹{selectedApplication.fee}</p>
                        </div>
                        <div>
                          <label className="label">
                            <span className="label-text font-semibold">Applied On</span>
                          </label>
                          <p className="text-base-content">
                            {formatDate(selectedApplication.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="modal-action">
              <button 
                onClick={() => handleDelete(selectedApplication?.id)}
                className="btn btn-error"
              >
                Delete Application
              </button>
              <button 
                onClick={handleCloseModal}
                className="btn btn-ghost"
              >
                Close
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={handleCloseModal}>close</button>
          </form>
        </dialog>
      </div>
    </div>
  );
};

export default DisplayApplications;